// Card — header / body / footer as REAL slots.
//
// This is the one place the React port deliberately refuses to inherit a
// Figma constraint. In Figma, instances are structurally locked: appendChild
// into a Card body throws, which is why ~56 component copies on the screen
// pages are detached (a card holding real content had to be detached to
// receive it). A React card that takes children is the whole point, so the
// body makes no assumption about what goes in it — a table, a list, a form,
// anything — and needs no escape hatch.
//
// Card is also the archetype with NO authoritative Figma spec: every copy
// read was detached, and the two disagreed (33:1370 is --surface-2 / padding
// 24 / Elevation-1, 33:2666 is --surface-1 / padding 16 / flat). Resolved as
// raised-by-default with `tone="flat"` for the other, rather than shipping
// two components. See ui.css.

import "./ui.css";
import type { ReactNode } from "react";

export type CardHeaderProps = {
  /** Rendered in the small-caps mono treatment of frame 33:1370 ("MOST
   *  CONFUSING TERMS"). Pass normal prose — the uppercase comes from CSS. */
  label: string;
  actions?: ReactNode;
};

export function CardHeader({ label, actions }: CardHeaderProps) {
  return (
    <div className="maisight-ui-card-header">
      <h2 className="maisight-ui-card-header-label type-label">{label}</h2>
      {actions}
    </div>
  );
}

export type CardProps = {
  /** A string gets the CardHeader treatment; a node is rendered verbatim. */
  header?: ReactNode;
  footer?: ReactNode;
  tone?: "raised" | "flat";
  children: ReactNode;
};

export function Card({ header, footer, tone = "raised", children }: CardProps) {
  return (
    <section className="maisight-ui-card" data-tone={tone}>
      {typeof header === "string" ? <CardHeader label={header} /> : header}
      <div className="maisight-ui-card-body">{children}</div>
      {footer ? <div className="maisight-ui-card-footer">{footer}</div> : null}
    </section>
  );
}
